import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Grid,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { authApi } from '../api/client';

const steps = ['Virksomhedsinfo', 'Kontaktperson', 'Bekræft'];

export const ApplyOrganizationPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    cvr: '',
    email: '',
    phone: '',
    address: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Adgangskoderne matcher ikke');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await authApi.applyOrganization({
        name: formData.name,
        cvr: formData.cvr || undefined,
        email: formData.email,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        contactFirstName: formData.contactFirstName,
        contactLastName: formData.contactLastName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone || undefined,
        password: formData.password,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ansøgningen kunne ikke sendes. Prøv igen.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="success.main">
              Ansøgning modtaget!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Vi har modtaget din ansøgning og vil gennemgå den hurtigst muligt.
              Du vil modtage en email, når din organisation er godkendt.
            </Typography>
            <Button variant="contained" component={RouterLink} to="/login">
              Tilbage til login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight={700}>
            Ansøg om adgang
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Udfyld formularen for at oprette din organisation
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <TextField
                fullWidth
                label="Virksomhedsnavn"
                value={formData.name}
                onChange={handleChange('name')}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="CVR-nummer"
                value={formData.cvr}
                onChange={handleChange('cvr')}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Virksomhedens email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Telefon"
                value={formData.phone}
                onChange={handleChange('phone')}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Adresse"
                value={formData.address}
                onChange={handleChange('address')}
                sx={{ mb: 2 }}
              />
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Fornavn"
                    value={formData.contactFirstName}
                    onChange={handleChange('contactFirstName')}
                    required
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Efternavn"
                    value={formData.contactLastName}
                    onChange={handleChange('contactLastName')}
                    required
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.contactEmail}
                onChange={handleChange('contactEmail')}
                required
                sx={{ mt: 2, mb: 2 }}
              />
              <TextField
                fullWidth
                label="Telefon"
                value={formData.contactPhone}
                onChange={handleChange('contactPhone')}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Adgangskode"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                required
                helperText="Minimum 8 tegn"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Bekræft adgangskode"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                required
              />
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Bekræft dine oplysninger
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Virksomhed:</strong> {formData.name}
              </Typography>
              {formData.cvr && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>CVR:</strong> {formData.cvr}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email:</strong> {formData.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Kontaktperson:</strong> {formData.contactFirstName} {formData.contactLastName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Kontakt email:</strong> {formData.contactEmail}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Tilbage
            </Button>
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext}>
                Næste
              </Button>
            ) : (
              <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Sender...' : 'Send ansøgning'}
              </Button>
            )}
          </Box>

          <Typography variant="body2" align="center" sx={{ mt: 3 }}>
            Har du allerede en konto?{' '}
            <Link component={RouterLink} to="/login">
              Log ind
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
